import { AdaEmbedAPI, StartOptions } from '@ada-support/embed-types/src';
import { useCallback, useEffect, useRef, useState } from 'react';

interface UseAdaChatBotProps {
    enabled: boolean;
    options: StartOptions;
    onError?: (error: Error) => void;
    startOnLoad?: boolean;
    isAdaChatBotV2Enabled: boolean;
}

enum SupportedLanguage {
    English = 'en',
    French = 'fr',
    German = 'de',
    Spanish = 'es',
    Dutch = 'nl',
}

const supportedLanguages: String[] = Object.values(SupportedLanguage);

const defaultLanguage = SupportedLanguage.English;

const CHAT_BUTTON_ID = '#ada-button-frame';

const CHAT_BUTTON_HIDDEN_CLASS = 'hidden';

const getSupportedLanguage = (language?: string) =>
    language !== undefined && supportedLanguages.includes(language) ? language : defaultLanguage;

export const useAdaChatBot = ({
    enabled,
    options,
    onError,
    startOnLoad = false,
    isAdaChatBotV2Enabled,
}: UseAdaChatBotProps) => {
    const [isStarted, setIsStarted] = useState(false);
    const [isChatOpen, setIsChatOpen] = useState(false);
    const adaEmbed = useRef<AdaEmbedAPI | null>(null);
    const adaChatButton = useRef<HTMLElement | null>(null);

    const showChatButton = useCallback(() => {
        adaChatButton.current?.classList.remove(CHAT_BUTTON_HIDDEN_CLASS);
    }, []);

    const hideChatButton = useCallback(() => {
        adaChatButton.current?.classList.add(CHAT_BUTTON_HIDDEN_CLASS);
    }, []);

    const start = useCallback(async () => {
        if (!isStarted && enabled) {
            try {
                if (!adaEmbed.current) {
                    const adaEmbedModule = await import(/* webpackChunkName: "adaChatBot" */ '@ada-support/embed2');
                    adaEmbed.current = adaEmbedModule.default;
                }

                await adaEmbed.current?.start({
                    ...options,
                    language: getSupportedLanguage(options.language),
                    adaReadyCallback: ({ isRolledOut }) => {
                        setIsStarted(isRolledOut);
                        if (isAdaChatBotV2Enabled) {
                            adaChatButton.current = document.querySelector(CHAT_BUTTON_ID);
                            void adaEmbed.current?.toggle();
                        }
                    },
                    toggleCallback: (isDrawerOpen) => {
                        setIsChatOpen(isDrawerOpen);
                    },
                    conversationEndCallback: () => {
                        if (isAdaChatBotV2Enabled) {
                            hideChatButton();
                        }
                    },
                });
            } catch (error) {
                if (onError) {
                    onError(error);
                }
            }
        }
    }, [isStarted, enabled, options, isAdaChatBotV2Enabled, hideChatButton, onError]);

    const stop = useCallback(async () => {
        if (isStarted && adaEmbed.current) {
            try {
                await adaEmbed.current.stop();
                setIsStarted(false);
            } catch (error) {
                if (onError) {
                    onError(error);
                }
            }
        }
    }, [isStarted, onError]);

    const toggle = useCallback(() => {
        if (isStarted && adaEmbed.current) {
            void adaEmbed.current.toggle();
        }
    }, [isStarted]);

    useEffect(() => {
        if (startOnLoad) {
            void start();
        }

        return () => {
            void stop();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        start,
        stop,
        toggle,
        isStarted,
        isChatOpen,
        isChatButtonHidden: adaChatButton.current?.classList.contains('hidden') ?? false,
        hideChatButton,
        showChatButton,
    };
};
